import {
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild
} from '@angular/core';
import { takeUntil } from 'rxjs/operators';
import { Subject } from 'rxjs';
import { AuctionService, DateUtilsService, KeepDrawerOpen, DateTimeFormat } from 'remarketing-angular-library';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import { CurrencyFormatService } from 'smartauction-frontend-micro-library';
import { TranslocoDirective } from '@jsverse/transloco';
import { CommonModule } from '@angular/common';

export interface AuctionItemRemovalHistory {
  saleFee: number;
  buyNowAmount: number;
  currentAmount: number;
  postedDate: string;
  removalReason: string;
  statusChangedDate: string;
  viewCount: number;
  highestOffer: number;
  bidListAdds: number;
  inventoryItemUuid: string;
}

@Component({
    selector: 'lib-removal-history',
    templateUrl: './removal-history.component.html',
    styleUrls: ['./removal-history.component.scss'],
    imports: [TranslocoDirective, CommonModule]
})
export class RemovalHistoryComponent implements OnInit, OnDestroy {
  @Input() auctionItemUuid: string;
  @Output() close = new EventEmitter();
  @ViewChild('heading') heading: ElementRef<HTMLHeadingElement>;

  removalHistory: AuctionItemRemovalHistory[];

  private _isVisible = false;

  @Input() set isVisible(isVisible: boolean) {
    this._isVisible = isVisible;
    if (this._isVisible) {
      setTimeout(() => this.heading.nativeElement.focus());
    }
  }
  get isVisible() {
    return this._isVisible;
  }

  private readonly destroyed$ = new Subject();
  showRemovedHistory: boolean;
  auctionItemSell: any;

  constructor(
    private readonly auctionItemService: AuctionService,
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly router: Router,
    private readonly store: Store,
    readonly dateUtilService: DateUtilsService,
    private readonly currencyFormatService: CurrencyFormatService
  ) {}

  ngOnInit() {
    this.getRemovalHistory();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getRemovalHistory() {
    this.auctionItemService
      .getAuctionItemSellRemovalHistory(this.auctionItemUuid)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(history => {
        this.removalHistory = history as AuctionItemRemovalHistory[];
        // Sort by desc dates
        this.removalHistory.sort((a, b) => {
          return Date.parse(b.statusChangedDate) - Date.parse(a.statusChangedDate);
        });
        this.changeDetectorRef.markForCheck();
        if (this.removalHistory.length === 0) {
          this.closeHistory();
        }
      });
  }

  localizeDate(date) {
    return this.dateUtilService.localizeDate(date);
  }

  seeDetails(i: number) {
    this.store.dispatch(new KeepDrawerOpen(this.auctionItemUuid));
    this.router.navigate([`/inventory/${this.removalHistory[i].inventoryItemUuid}/details`]);
  }

  closeHistory() {
    this.close.emit();
  }

  getFormattedRemovedTime(entry: AuctionItemRemovalHistory) {
    return this.dateUtilService.formatDate(entry.statusChangedDate, DateTimeFormat.H_MM_A, true);
  }

  getFormattedPostedDate(entry: AuctionItemRemovalHistory) {
    return this.dateUtilService.formatDate(entry.postedDate);
  }

  formatDate(date) {
    return this.dateUtilService.localizeDate(date);
  }

  formatCurrency(value) {
    return this.currencyFormatService.formatCurrency(value);
  }

  formatTime(entry: AuctionItemRemovalHistory) {
    return this.dateUtilService.localizeTime(entry.statusChangedDate, true);
  }

  formatDateTime(entry: AuctionItemRemovalHistory) {
    return this.dateUtilService.localizeDateAndTime(entry.statusChangedDate, true);
  }
}
